//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { FormControl, FormControlLabel, makeStyles, Popover, Radio, RadioGroup } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _toLower from 'lodash/toLower';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
// import _slice from 'lodash/slice';
// import _findIndex from 'lodash/findIndex';
// import _includes from 'lodash/includes';
// import _some from 'lodash/some';
// import _keys from 'lodash/keys';
// import _forEach from 'lodash/forEach';
// import _remove from 'lodash/remove';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    PLACEHOLDER_FIELD_JOB_DESCRIPTION,
    PLACEHOLDER_FIELD_EMPLOYMENT_TYPE_SINGLE,
    PLACEHOLDER_FIELD_TELEWORK,
    PLACEHOLDER_FIELD_EDUCATION,
    PLACEHOLDER_FIELD_OTHER_EDUCATION,
    PLACEHOLDER_FIELD_PAY_FREQUENCY,
    PLACEHOLDER_FIELD_JOB_TITLE,
    PLACEHOLDER_FIELD_JOB_ID_REQUISITION_NUMBER,
    PLACEHOLDER_FIELD_BENEFITS_SUMMARY,
    PLACEHOLDER_FIELD_COMPANY_BIO,
} from '../../common/placeholderHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import useRequiredFields from '../../common/hooks/useRequiredFields';
import {
    CONFIRM_DIALOG_TITLE_CANCEL,
    CONFIRM_DIALOG_MESSAGE_CANCEL,
    CONFIRM_DIALOG_TITLE_CLEAR_ALL,
    CONFIRM_DIALOG_MESSAGE_CLEAR_ALL,
    SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED,
    ANALYTICS_EVENT_JOB_LISTING_CREATED,
    ANALYTICS_EVENT_JOB_LISTING_SET_ACTIVE,
    // ERROR_MESSAGE_INVALID_URL,
} from '../../common/constants';
import { getCreatableLovSuggestions } from '../../common/lovHelper';
import { ELASTICSEARCH_TYPE_OTHER_EDUCATION } from '../../common/elasticSearchHelper';
import { getEmployerDataForActiveJob } from '../../common/employerHelper';

// import { isValidUrl } from '../../common/linkHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { /*loadStatesList, loadCitiesList,*/ saveNewJob, updateJob } from '../../common/services/empMyJobsServices';
import { saveProfileBenefitBio } from '../../common/services/empMyProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocompleteLite';
import AfitdSalaryRate from '../../common/elemental/AfitdSalaryRate';
import AfitdCheckbox from '../../common/elemental/AfitdCheckbox';

import AfitdAutocompleteLiteCreatable from '../../common/elemental/AfitdAutocompleteLiteCreatable';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdStateAutocomplete from '../../common/elemental/AfitdStateAutocomplete';
import AfitdCityAutocomplete from '../../common/elemental/AfitdCityAutocomplete';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdCurrencyAutocomplete from '../../common/elemental/AfitdCurrencyAutocomplete';
import AfitdYearsOfExperienceAutocomplete from '../../common/elemental/AfitdYearsOfExperienceAutocomplete';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import { moderateUserTextInput } from '../../common/sightEngineHelper';
import ConfirmationDialog from './ConfirmationDialog';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = ({ activeStr, comingSoon, filled } = {}) => {
    const isJobActive = activeStr === 'active';

    const labelStyles = { padding: '5px 0', fontWeight: 'bold' };
    const checkboxContainerStyles = { marginTop: 20, marginLeft: -5, display: 'flex', alignItems: 'center' };

    const infoPopoverStyles = { '& .MuiPaper-root': { width: 200, padding: 10 } };
    const infoIconStyles = { fontSize: 20, margin: '0 0 0 10px', cursor: 'pointer' };

    return makeStyles(theme => ({
        multiFieldContainer: { display: 'flex', margin: '10px 0' },
        inputBoxContainer: { flex: 1, padding: '0 5px', marginTop: 20 },
        inputBoxContainerSlim: { flex: 1, padding: '0 5px', marginTop: 10 },
        comingSoonCheckboxContainer: checkboxContainerStyles,
        filledCheckboxContainer: checkboxContainerStyles,
        internshipCheckboxContainer: { ...checkboxContainerStyles, marginTop: 12 },
        label: labelStyles,
        comingSoonLabel: { ...labelStyles, ...(isJobActive && !filled ? {} : { color: 'rgba(17, 30, 20, 0.4)' }) },
        filledLabel: { ...labelStyles, ...(isJobActive && !comingSoon ? {} : { color: 'rgba(17, 30, 20, 0.4)' }) },
        otherLabel: { padding: '5px 0', fontWeight: 'bold' },
        active: { color: 'rgb(29,138,139)' },
        inactive: { color: 'rgba(17,30,20,0.4)' },
        radioGroup: { '& .MuiFormControlLabel-labelPlacementStart': { margin: 0 } },
        hidden: { display: 'none' },
        helpIcon: { color: 'black' },
        urlMisc: { color: 'rgba(17, 30, 20, 0.4)', margin: '0 10px' },
        comingSoonInfoPopover: infoPopoverStyles,
        comingSoonInfoIcon: infoIconStyles,
        filledInfoPopover: infoPopoverStyles,
        filledInfoIcon: infoIconStyles,
        popover: infoPopoverStyles,
        infoIcon: infoIconStyles,
        inline: { display: 'flex', alignItems: 'center' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        isDirty,
        handleSaveButton,
        data,
        // country,
        // state,
        // city,
        countryCurrency,
        handleChangeFields,
        handleChangeCompanyInfoFields,
        handleChangeAutoCompleteFields,
        handleChangeActiveInactive,
        handleChangeComingSoon,
        handleChangeFilled,
        handleChangeIntern,
        handleChangeYearsOfExperience,
        handleChangeSalaryRate,
        handleChangeCurrency,
        handleChangeJobApplicationUrl,
        handleClearAllButton,
        showClearAllModal,
        setShowClearAllModal,
        isLimitModalOpen,
        setIsLimitModalOpen,
        // isJobApplicationUrlValid,
        lovs,
        confirmModal,
        setConfirmModal,
        // prevData,
        comingSoonInfoPopoverOpen,
        setComingSoonInfoPopoverOpen,
        filledInfoPopoverOpen,
        setFilledInfoPopoverOpen,
        popover,
        setPopover,
        // setData,
        job,
        companyInfoData,
        inactiveModal,
        setInactiveModal,
        handleMakeJobInactiveButton,
        countryProps,
        stateProps,
        cityProps,
        stateLabelProps,

        setSalaryRateIsValid,
        // invalidRequiredFields,
        // setInvalidRequiredFields,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        openBadWordsDialog,
        badWords,
        clearOutBadWords,
        saveLoading,
        otherEducationOptions,
        setOtherEducationOptions,
        esToken,
        setEsToken,
    } = params;
    const { show, close } = props;
    const {
        active,
        reqNum,
        comingSoon,
        filled,
        intern,
        jobTitle,
        yrsOfExp,
        employmentType,
        currency,
        currencySymbol,
        telework,
        education,
        otherEducation,
        jobDescription,
        isExactRate,
        rateMin,
        rateMax,
        payFrequency,
        // otherSalary,
        jobApplicationUrl,
    } = data;
    const { active: activeCls, inactive: inactiveCls, /*hidden: hiddenCls,*/ otherLabel: otherLabelCls } = classes;

    const { payFrequencyLov, teleworkLov, educationLov, employmentTypeLov } = lovs;

    const otherEducationLov = _get(lovs, 'otherEducationLov', []);
    const badWordsList = badWords?.profanity?.join(', ');

    const saveButtonDisabled = !isDirty;

    const benefitSummary = companyInfoData?.benefitSummary;
    const companyBio = companyInfoData?.companyBio;

    // const isChange = Object.keys({ ...data, country, state, city }).every(key => _isEqual(data[key], prevData.current[key]));

    // const payFrequencyLOV = getPayFrequencyList();
    // const teleworkLOV = getTeleworkList();

    const defns = {
        container: {
            open: show,
            onClose: close,
            label: 'Job Requisition',
            actionButtonDefns: {
                clearAll: {
                    disabled: false,
                    onClick: () => (job?.active ? setInactiveModal(true) : setShowClearAllModal(true)),
                    label: 'Clear All',
                },
                cancel: {
                    disabled: false,
                    onClick: isDirty ? () => setConfirmModal(true) : close,
                },
                save: {
                    disabled: saveButtonDisabled,
                    onClick: handleSaveButton,
                    label: saveLoading ? (
                        <>
                            <CircularProgress color="#ffffff" size={20} />
                            &nbsp; Saving
                        </>
                    ) : (
                        'Save'
                    ),
                    color: 'primary',
                },
            },
        },
        reqNum: { onChange: handleChangeFields, name: 'reqNum', error: getFieldError('reqNum'), value: reqNum, placeholder: PLACEHOLDER_FIELD_JOB_ID_REQUISITION_NUMBER },
        comingSoon: {
            checked: comingSoon,
            onChange: handleChangeComingSoon,
            disabled: active !== 'active' || filled,
        },
        filled: {
            checked: filled,
            onChange: handleChangeFilled,
            disabled: active !== 'active' || comingSoon,
        },
        intern: {
            checked: intern,
            onChange: handleChangeIntern,
        },
        jobTitle: { onChange: handleChangeFields, name: 'jobTitle', error: getFieldError('jobTitle'), value: jobTitle, placeholder: PLACEHOLDER_FIELD_JOB_TITLE },
        yrsOfExp: {
            value: yrsOfExp,
            onChange: handleChangeYearsOfExperience,
        },
        employmentType: {
            onChange: (i, evt) => handleChangeAutoCompleteFields('employmentType', evt),
            name: 'employmentType',
            error: getFieldError('employmentType'),
            fullWidth: true,
            value: employmentType,
            placeholder: PLACEHOLDER_FIELD_EMPLOYMENT_TYPE_SINGLE,
            options: employmentTypeLov,
        },
        telework: {
            onChange: (i, evt) => handleChangeAutoCompleteFields('telework', evt),
            name: 'telework',
            error: getFieldError('telework'),
            value: telework,
            placeholder: PLACEHOLDER_FIELD_TELEWORK,
            fullWidth: true,
            options: teleworkLov,
        },
        education: {
            onChange: (i, evt) => handleChangeAutoCompleteFields('education', evt),
            name: 'education',
            error: getFieldError('education'),
            value: education,
            placeholder: PLACEHOLDER_FIELD_EDUCATION,
            fullWidth: true,
            options: educationLov,
        },
        otherEducation: {
            noOptionsText: 'Type and select other education',
            options: otherEducationOptions,
            placeholder: PLACEHOLDER_FIELD_OTHER_EDUCATION,
            isMulti: true,
            value: otherEducation,
            onChange: (i, evt) => handleChangeAutoCompleteFields('otherEducation', evt),
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getCreatableLovSuggestions({ type: ELASTICSEARCH_TYPE_OTHER_EDUCATION, keywords: value, esToken, setEsToken });
                    setOtherEducationOptions(options);
                } else setOtherEducationOptions(otherEducationLov);
            },
        },

        jobDescription: {
            onChange: handleChangeFields,
            name: 'jobDescription',
            error: getFieldError('jobDescription'),
            value: jobDescription,
            placeholder: PLACEHOLDER_FIELD_JOB_DESCRIPTION,
            multiline: true,
        },

        benefitSummary: {
            onChange: handleChangeCompanyInfoFields,
            name: 'benefitSummary',
            // error: getFieldError('xxxxx'),
            value: benefitSummary,
            placeholder: PLACEHOLDER_FIELD_BENEFITS_SUMMARY,
            multiline: true,
        },

        companyBio: {
            onChange: handleChangeCompanyInfoFields,
            name: 'companyBio',
            // error: getFieldError('xxxxx'),
            value: companyBio,
            placeholder: PLACEHOLDER_FIELD_COMPANY_BIO,
            multiline: true,
        },

        jobApplicationUrl: {
            onChange: handleChangeJobApplicationUrl,
            name: 'jobApplicationUrl',
            // error: !isJobApplicationUrlValid,
            // helperText: isJobApplicationUrlValid ? '' : ERROR_MESSAGE_INVALID_URL,
            value: jobApplicationUrl,
            placeholder: '',
            multiline: true,
        },

        currency: {
            value: currency,
            error: getFieldError('currency'),
            onChange: (i, arr) => handleChangeCurrency(arr),
            countryCurrency,
        },

        otherEduLabel: { className: otherLabelCls },

        payFrequency: {
            onChange: (i, evt) => handleChangeAutoCompleteFields('payFrequency', evt),
            value: payFrequency,
            fullWidth: true,
            name: 'payFrequency',
            error: getFieldError('payFrequency'),
            placeholder: PLACEHOLDER_FIELD_PAY_FREQUENCY,
            options: payFrequencyLov,
        },
        salaryRate: {
            isExactRate,
            rateMin,
            rateMax,
            rateMinError: getFieldError('rateMin'),
            rateMaxError: getFieldError('rateMax'),
            currencySymbol,
            onChange: handleChangeSalaryRate,
            setIsValid: setSalaryRateIsValid,
        },

        country: { ...countryProps },
        stateLabel: {
            ...stateLabelProps,
        },
        state: { ...stateProps },
        city: { ...cityProps },

        radioGroup: { row: true, value: active, name: 'active', onChange: handleChangeActiveInactive },
        activeLabel: { value: 'active', control: <Radio disableRipple />, label: 'Active', labelPlacement: 'start', className: active === 'active' ? activeCls : inactiveCls },
        inactiveLabel: {
            value: 'inactive',
            control: <Radio disableRipple />,
            label: 'Inactive',
            labelPlacement: 'start',
            className: active === 'inactive' ? activeCls : inactiveCls,
        },
        clearAllDialog: {
            open: showClearAllModal,
            onClose: () => setShowClearAllModal(false),
            onConfirm: handleClearAllButton,
            title: CONFIRM_DIALOG_TITLE_CLEAR_ALL,
            message: CONFIRM_DIALOG_MESSAGE_CLEAR_ALL,
        },
        limitDialog: {
            open: isLimitModalOpen,
            onClose: () => setIsLimitModalOpen(false),
            onConfirm: () => setIsLimitModalOpen(false),
            message: 'Your limit of simultaneous Active jobs have been exceeded. Please upgrade your subscription to post more job listings.',
            confirmLabel: 'Upgrade',
            title: 'Interaction Limit Exceeded',
        },
        confirmCancel: {
            open: confirmModal,
            onClose: () => setConfirmModal(false),
            onConfirm: () => {
                handleClearAllButton();
                setConfirmModal(false);
                close();
            },
            message: CONFIRM_DIALOG_MESSAGE_CANCEL,
            title: CONFIRM_DIALOG_TITLE_CANCEL,
        },

        tooltip: { title: 'If your company has a mandatory job application online that must be completed, please add the URL here' },

        comingSoonInfoIcon: { onClick: e => setComingSoonInfoPopoverOpen(e.target), 'aria-describedby': 'coming-soon-info-pop-over' },
        comingSoonInfoPopover: {
            id: 'coming-soon-info-pop-over',
            anchorEl: comingSoonInfoPopoverOpen,
            open: comingSoonInfoPopoverOpen,
            onClose: () => setComingSoonInfoPopoverOpen(null),
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
        },
        filledInfoIcon: { onClick: e => setFilledInfoPopoverOpen(e.target), 'aria-describedby': 'coming-soon-info-pop-over' },
        filledInfoPopover: {
            id: 'filled-info-pop-over',
            anchorEl: filledInfoPopoverOpen,
            open: filledInfoPopoverOpen,
            onClose: () => setFilledInfoPopoverOpen(null),
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
        },
        infoIcon: { onClick: e => setPopover(e.target), 'aria-describedby': 'pop-over' },
        popover: {
            id: 'pop-over',
            anchorEl: popover,
            open: popover,
            onClose: () => setPopover(null),
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
        },

        makeInactiveDialog: {
            open: inactiveModal,
            onClose: () => setInactiveModal(false),
            onConfirm: handleMakeJobInactiveButton,
            message: 'You cannot erase the information from this job listing',
            title: 'This Job Listing is Active',
            color: 'primary',
            confirmLabel: 'Make Inactive',
        },

        pageErrorDialog: {
            message: 'You must enter all required fields.',
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AddJobDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const { currentEmployer, employerId, lovs, esToken, setEsToken } = useContext(AppContext);

    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { show, close, limitExceeded, job, profileData, onJobAdd, onJobUpdate } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [showClearAllModal, setShowClearAllModal] = useState(false);
    const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [inactiveModal, setInactiveModal] = useState(false);
    const [salaryRateIsValid, setSalaryRateIsValid] = useState(true);
    const [pageErrorDialogOpen, setPageErrorDialogOpen] = useState(false);
    const { invalidRequiredFields, setInvalidRequiredFields, getFieldError, clearInvalidRequiredField, validateRequiredFields } = useRequiredFields();
    const [isJobApplicationUrlValid] = useState(true);

    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);

    const [comingSoonInfoPopoverOpen, setComingSoonInfoPopoverOpen] = useState(false);
    const [filledInfoPopoverOpen, setFilledInfoPopoverOpen] = useState(false);
    const [popover, setPopover] = useState(false);
    const [otherEducationOptions, setOtherEducationOptions] = useState([]);

    // const [statesLov, setStatesLov] = useState([]);
    // const [citiesLov, setCitiesLov] = useState([]);

    const [data, setData] = useState({
        active: 'active',
        reqNum: '',
        comingSoon: false,
        filled: false,
        intern: false,
        jobTitle: '',
        yrsOfExp: null,
        employmentType: null,
        currency: null,
        currencySymbol: null,
        isExactRate: false,
        rateMin: null,
        rateMax: null,
        payFrequency: null,
        telework: null,
        education: null,
        otherEducation: [],
        jobDescription: '',
        jobApplicationUrl: 'https://',
    });

    const [companyInfoData, setCompanyInfoData] = useState({
        benefit: null,
        bio: null,
    });

    // // this ref default to 'data'
    // let prevData = useRef({ ...data, country, state, city });

    //---------------------------------------------------------------------
    // useCountryStateCity hook
    //---------------------------------------------------------------------
    const {
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        // setCityLocation,
        countryCurrency,
        setCountryStateCity,
        countryProps,
        stateProps,
        cityProps,
        stateLabelProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov', []),
        setIsDirty,
        invalidRequiredFields,
        setInvalidRequiredFields,
        clearInvalidRequiredField,
        getFieldError,
    });

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const employerData = getEmployerDataForActiveJob({ employer: currentEmployer, profile: profileData });
    const benefitSummary = employerData?.benefitSummary;
    const companyBio = employerData?.companyBio;
    const activeStr = _get(data, 'active');
    const comingSoon = data.comingSoon;
    const filled = data.filled;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (show) {
            setIsDirty(false);
        }
    }, [show]);

    useEffect(() => {
        if (show && job) {
            const { active } = job;
            // prevData.current = { ...job, active: active ? 'active' : 'inactive' };
            setData({
                ...job,
                active: active ? 'active' : 'inactive',
            });
            setCountryStateCity({
                country: _get(job, 'country'),
                state: _get(job, 'state'),
                city: _get(job, 'city'),
                cityLocation: _get(job, 'cityLocation'),
            });
        }
        setCompanyInfoData({ benefitSummary, companyBio });
    }, [show, job, benefitSummary, companyBio, setCountryStateCity]);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const getPageIsValid = useCallback(() => {
        const { jobTitle, employmentType, currency, rateMin, rateMax, payFrequency, telework, education, jobDescription } = data;
        const requiredFields = {
            jobTitle,
            employmentType,
            currency,
            rateMin,
            rateMax,
            payFrequency,
            telework,
            education,
            jobDescription,
            country,
            state,
            city,
        };

        return validateRequiredFields(requiredFields) && salaryRateIsValid;
    }, [salaryRateIsValid, data, country, state, city, validateRequiredFields]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField(name);
    };

    const handleChangeCompanyInfoFields = evt => {
        const { name, value } = evt.target;
        setCompanyInfoData(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
        // clear error highlight
        // clearInvalidRequiredField(name);
    };

    const handleChangeAutoCompleteFields = (name, value) => {
        setData({ ...data, [name]: value });
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField(name);
    };

    const handleChangeActiveInactive = evt => {
        const { value } = evt.target;
        setData({ ...data, active: value, comingSoon: false, filled: false });
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('active');
    };

    const handleChangeComingSoon = (evt, value) => {
        setData({ ...data, comingSoon: value, filled: false });
        setIsDirty(true);
    };

    const handleChangeFilled = (evt, value) => {
        setData({ ...data, filled: value, comingSoon: false });
        setIsDirty(true);
    };

    const handleChangeIntern = (evt, value) => {
        setData({ ...data, intern: value });
        setIsDirty(true);
    };

    const handleChangeYearsOfExperience = (evt, option) => {
        const minVal = option?.minVal;
        setData(prev => ({ ...prev, yrsOfExp: minVal }));
        setIsDirty(true);
    };

    const handleChangeSalaryRate = (value, name, isInvalidRange) => {
        // const { isExactRate, rateMin, rateMax } = value;
        setData({ ...data, ...value });
        setIsDirty(true);
        // clear error highlight (only if the range is valid)
        if (!isInvalidRange) {
            clearInvalidRequiredField(name);
        }
    };

    const handleChangeCurrency = option => {
        setData({ ...data, currency: option?.code, currencySymbol: option?.symbol_native });
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('currency');
    };

    const handleChangeJobApplicationUrl = evt => {
        const { name, value: inputValue } = evt.target;
        const value = _trim(inputValue);
        setData({ ...data, [name]: value });
        setIsDirty(true);
        // validate URL
        if (value === '') setData({ ...data, [name]: 'https://' });
    };

    const cleanUpAndClose = () => {
        close();
        handleClearAllButton();
    };

    const handleSaveButton = async () => {
        const dto = { ...data, country, state, city, cityLocation };

        const benefitSummaryLocal = companyInfoData.benefitSummary;
        const companyBioLocal = companyInfoData.companyBio;
        const companyInfoChanged = !_isEqual(benefitSummary, benefitSummaryLocal) || !_isEqual(companyBio, companyBioLocal);

        const result = Object.keys(dto).reduce((acc, key) => (dto[key] === undefined ? { ...acc, [key]: '' } : { ...acc, [key]: dto[key] }), {});
        if (job) {
            const { active } = job;
            if (getPageIsValid()) {
                setSaveLoading(true);
                // const reqNum = _get(data, 'reqNum');
                const jobTitle = _get(data, 'jobTitle');
                // const jobDescription = _get(data, 'jobDescription');
                const activeStr = _get(data, 'active');
                const isJobActive = activeStr === 'active';
                const jobApplicationUrl = _get(data, 'jobApplicationUrl');
                let otherEducation = _get(data, 'otherEducation');
                otherEducation = otherEducation?.map(el => {
                    if (el?.inputValue) return el?.inputValue;
                    else return el;
                });

                // dto for sight Engine
                const dto = {
                    jobTitle,
                    // jobDescription: jobDescription.replace(/\r?\n|\r/g, ''),
                    // otherEducation: otherEducation?.map(el => el?.inputValue),
                };

                const response = await moderateUserTextInput({ text: JSON.stringify(dto) });

                if (!response) {
                    setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
                    setSaveLoading(false);
                } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
                    setOpenBadWordsDialog(true);
                    setBadWords(response);
                    setSaveLoading(false);
                } else {
                    const dto = {
                        ...result,
                        ...employerData,
                        benefitSummary: benefitSummaryLocal,
                        companyBio: companyBioLocal,
                        active: isJobActive,
                        jobApplicationUrl: jobApplicationUrl === 'https://' ? '' : jobApplicationUrl,
                        otherEducation,
                    };
                    const updatedJob = await updateJob({
                        data: dto,
                        activeOrig: active,
                        setSnackbarMessage,
                        setSnackbarErrorMessage,
                    });

                    !!updatedJob && onJobUpdate && onJobUpdate(updatedJob);

                    if (companyInfoChanged) {
                        await saveProfileBenefitBio({ employerId, benefit: benefitSummaryLocal, bio: companyBioLocal });
                    }

                    setSaveLoading(false);
                    cleanUpAndClose();
                    // isJobActive && logEvent(ANALYTICS_EVENT_JOB_LISTING_SET_ACTIVE);
                }
            } else {
                setSaveLoading(false);
                setPageErrorDialogOpen(true);
            }
        } else if (limitExceeded) {
            setIsLimitModalOpen(true);
        } else {
            if (getPageIsValid()) {
                setSaveLoading(true);
                // const reqNum = _get(data, 'reqNum');
                const jobTitle = _get(data, 'jobTitle');
                // const jobDescription = _get(data, 'jobDescription');
                const activeStr = _get(data, 'active');
                const isJobActive = activeStr === 'active';
                const jobApplicationUrl = _get(data, 'jobApplicationUrl');
                let otherEducation = _get(data, 'otherEducation');
                otherEducation = otherEducation?.map(el => {
                    if (el?.inputValue) return el?.inputValue;
                    else return el;
                });

                const dto = {
                    jobTitle,
                    // jobDescription: jobDescription.replace(/\r?\n|\r/g, ''),
                    // otherEducation,
                };
                const response = await moderateUserTextInput({ text: JSON.stringify(dto) });

                if (!response) {
                    setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
                    setSaveLoading(false);
                } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
                    setOpenBadWordsDialog(true);
                    setBadWords(response);
                    setSaveLoading(false);
                } else {
                    const dto = { ...result, active: isJobActive, jobApplicationUrl: jobApplicationUrl === 'https://' ? '' : jobApplicationUrl, otherEducation };

                    const newJob = await saveNewJob(dto, employerId, employerData, setSnackbarMessage, setSnackbarErrorMessage, benefitSummaryLocal, companyBioLocal);
                    !!newJob && onJobAdd && onJobAdd(newJob);

                    if (companyInfoChanged) {
                        await saveProfileBenefitBio({ employerId, benefit: benefitSummaryLocal, bio: companyBioLocal });
                    }

                    setSaveLoading(false);
                    cleanUpAndClose();
                    if (!!newJob) {
                        logEvent(ANALYTICS_EVENT_JOB_LISTING_CREATED);
                        isJobActive && logEvent(ANALYTICS_EVENT_JOB_LISTING_SET_ACTIVE);
                    }
                }
            } else {
                setSaveLoading(false);
                setPageErrorDialogOpen(true);
            }
        }
    };

    const clearOutBadWords = () => {
        // eslint-disable-next-line no-unused-expressions
        badWords?.profanity?.forEach(badWord => {
            badWord.toLowerCase();
            const jobTitle = _get(data, 'jobTitle');
            // const jobDescription = _get(data, 'jobDescription');
            // const otherEducation = _get(data, 'otherEducation');

            const regEx = new RegExp(badWord, 'ig');

            if (jobTitle.toLowerCase().includes(badWord)) {
                const filteredJobTitle = jobTitle.replace(regEx, '');
                setData({ ...data, jobTitle: filteredJobTitle });
            }

            // if (jobDescription?.toLowerCase().includes(badWord)) {
            //     const filteredJobDescription = jobDescription.replace(regEx, '');
            //     setData({ ...data, jobDescription: filteredJobDescription });
            // }
            // if (
            //     otherEducation
            //         ?.map(el => el?.inputValue)
            //         ?.join('')
            //         .toLowerCase()
            //         .includes(badWord)
            // ) {
            //     const filteredOtherEducation = otherEducation?.filter(el => !el?.inputValue?.toLowerCase().includes(badWord));
            //     setData({ ...data, otherEducation: filteredOtherEducation });
            // }
        });
        setOpenBadWordsDialog(false);
        // setBadWords({});
    };

    const handleClearAllButton = () => {
        setShowClearAllModal(false);
        setData({
            active: 'active',
            reqNum: '',
            comingSoon: false,
            filled: false,
            intern: false,
            jobTitle: '',
            yrsOfExp: null,
            employmentType: null,
            currency: null,
            currencySymbol: null,
            country: null,
            state: null,
            city: null,
            telework: null,
            education: null,
            otherEducation: [],
            jobDescription: '',
            // rate: '',
            isExactRate: false,
            rateMin: null,
            rateMax: null,
            jobApplicationUrl: '',
        });
        setCountry(null);
        setState(null);
        setCity(null);
    };

    const handleMakeJobInactiveButton = () => {
        updateJob({ ...data, active: !(data.active === 'active') }, setSnackbarMessage, setSnackbarErrorMessage, cleanUpAndClose);
        setInactiveModal(false);
        close();
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ activeStr, comingSoon, filled })();
    const defns = getDefns({
        classes,
        props,
        isDirty,
        handleSaveButton,
        data,
        // country,
        // state,
        // city,
        cityLocation,
        countryCurrency,
        handleChangeFields,
        handleChangeCompanyInfoFields,
        handleChangeAutoCompleteFields,
        handleChangeActiveInactive,
        handleChangeComingSoon,
        handleChangeFilled,
        handleChangeIntern,
        handleChangeYearsOfExperience,
        handleChangeSalaryRate,
        handleChangeCurrency,
        handleChangeJobApplicationUrl,
        handleClearAllButton,
        showClearAllModal,
        setShowClearAllModal,
        isLimitModalOpen,
        setIsLimitModalOpen,
        isJobApplicationUrlValid,
        lovs,
        confirmModal,
        setConfirmModal,
        // prevData,
        comingSoonInfoPopoverOpen,
        setComingSoonInfoPopoverOpen,
        filledInfoPopoverOpen,
        setFilledInfoPopoverOpen,
        popover,
        setPopover,
        setData,
        job,
        companyInfoData,
        inactiveModal,
        setInactiveModal,
        handleMakeJobInactiveButton,
        countryProps,
        stateProps,
        cityProps,
        stateLabelProps,

        setSalaryRateIsValid,
        invalidRequiredFields,
        setInvalidRequiredFields,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        openBadWordsDialog,
        setOpenBadWordsDialog,
        badWords,
        setBadWords,
        clearOutBadWords,
        saveLoading,
        otherEducationOptions,
        setOtherEducationOptions,
        esToken,
        setEsToken,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <AfitdEditDialog {...defns.container}>
                <div {...defns.inputBoxContainer}>
                    <FormControl>
                        <RadioGroup {...defns.radioGroup}>
                            <FormControlLabel {...defns.activeLabel} />
                            <FormControlLabel {...defns.inactiveLabel} />
                        </RadioGroup>
                    </FormControl>
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Job ID/Req # (for internal use only)</AfitdTypography>
                    <AfitdTextField {...defns.reqNum} />
                </div>
                <div {...defns.comingSoonCheckboxContainer}>
                    <AfitdCheckbox {...defns.comingSoon} />
                    <AfitdTypography {...defns.comingSoonLabel}>Coming Soon</AfitdTypography>
                    <InfoOutlinedIcon {...defns.comingSoonInfoIcon} />
                    <Popover {...defns.comingSoonInfoPopover}>
                        <AfitdTypography>
                            {`Capture the attention of qualified candidates before the position officially opens. Job seekers cannot apply until you're ready. ${
                                filled ? " (To select this, please deselect 'Already Filled' first)" : ''
                            }`}
                        </AfitdTypography>
                    </Popover>
                </div>
                <div {...defns.internshipCheckboxContainer}>
                    <AfitdCheckbox {...defns.intern} />
                    <AfitdTypography {...defns.label}>Internship?</AfitdTypography>
                </div>
                <div {...defns.inputBoxContainerSlim}>
                    <AfitdTypography {...defns.label}>Job Title *</AfitdTypography>
                    <AfitdTextField {...defns.jobTitle} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Years of Experience</AfitdTypography>
                    <AfitdYearsOfExperienceAutocomplete {...defns.yrsOfExp} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Employment Type *</AfitdTypography>
                    <AfitdAutocomplete {...defns.employmentType} />
                </div>
                <div {...defns.multiFieldContainer}>
                    <div {...defns.inputBoxContainer}>
                        <AfitdTypography {...defns.label}>Currency *</AfitdTypography>
                        <AfitdCurrencyAutocomplete {...defns.currency} />
                    </div>
                    <div {...defns.inputBoxContainer}>
                        <AfitdTypography {...defns.label}>Pay Frequency *</AfitdTypography>
                        <AfitdAutocomplete {...defns.payFrequency} />
                    </div>
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Salary/Rate *</AfitdTypography>
                    <AfitdSalaryRate {...defns.salaryRate} />
                </div>
                <div {...defns.multiFieldContainer}>
                    <div {...defns.inputBoxContainer}>
                        <AfitdTypography {...defns.label}>Country *</AfitdTypography>
                        <AfitdCountryAutocomplete {...defns.country} />
                    </div>
                    <div {...defns.inputBoxContainer}>
                        <AfitdTypography {...defns.label}>{defns.stateLabel.value} *</AfitdTypography>
                        <AfitdStateAutocomplete {...defns.state} />
                    </div>
                </div>
                <div {...defns.multiFieldContainer}>
                    <div {...defns.inputBoxContainer}>
                        <AfitdTypography {...defns.label}>City *</AfitdTypography>
                        <AfitdCityAutocomplete {...defns.city} />
                    </div>
                    <div {...defns.inputBoxContainer}></div>
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Telework *</AfitdTypography>
                    <AfitdAutocomplete {...defns.telework} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Education Level *</AfitdTypography>
                    <AfitdAutocomplete {...defns.education} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.otherEduLabel}>Other Education, License(s), or Certification(s)</AfitdTypography>
                    <AfitdAutocompleteLiteCreatable {...defns.otherEducation} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Job Description *</AfitdTypography>
                    <AfitdTextField {...defns.jobDescription} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Benefit Summary</AfitdTypography>
                    <AfitdTextField {...defns.benefitSummary} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <AfitdTypography {...defns.label}>Company Bio</AfitdTypography>
                    <AfitdTextField {...defns.companyBio} />
                </div>
                <div {...defns.inputBoxContainer}>
                    <div {...defns.inline}>
                        <AfitdTypography {...defns.label}>Job Application URL (Optional)</AfitdTypography>
                        <InfoOutlinedIcon {...defns.infoIcon} />
                        <Popover {...defns.popover}>
                            <AfitdTypography>If your company has a mandatory job application online that must be completed, please add the URL here.</AfitdTypography>
                        </Popover>
                    </div>
                    <AfitdTextField {...defns.jobApplicationUrl} />
                    <span {...defns.urlMisc}>Ex: https://www.yourcompany.com/job-application</span>
                </div>
                <div {...defns.filledCheckboxContainer}>
                    <AfitdCheckbox {...defns.filled} />
                    <AfitdTypography {...defns.filledLabel}>Already Filled</AfitdTypography>
                    <InfoOutlinedIcon {...defns.filledInfoIcon} />
                    <Popover {...defns.filledInfoPopover}>
                        <AfitdTypography>
                            {`Maintain visibility & show active growth even after filling this position.${
                                comingSoon ? " (To select this, please deselect 'Coming Soon' first)" : ''
                            }`}
                            .
                        </AfitdTypography>
                    </Popover>
                </div>
            </AfitdEditDialog>
            <AfitdConfirmDialog {...defns.clearAllDialog} />
            <ConfirmationDialog {...defns.limitDialog} />
            <AfitdConfirmDialog {...defns.confirmCancel} />
            <ConfirmationDialog {...defns.makeInactiveDialog} />
            <AfitdMessageDialog {...defns.pageErrorDialog} />
            <AfitdMessageDialog {...defns.badWordsDialog} />
        </div>
    );
};

export default AddJobDialog;
